import type React from 'react';
import { type Goal, type GoalProgress } from '../../types/goals';
import { ProgressBar, StatusBadge } from '../molecules';
import { goalsService } from '../../services/goals';

interface GoalCardProps {
  goal: Goal;
  progress?: GoalProgress;
  className?: string;
  onClick?: () => void;
  showProgress?: boolean;
  showCategory?: boolean;
}

const getDeltaColorClass = (deltaValue: string): string => {
  if (deltaValue.startsWith('+')) {
    return 'bg-green-100 text-green-700';
  }
  if (deltaValue.startsWith('-')) {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-gray-100 text-gray-700';
};

const getProgressVariant = (progress: number): 'success' | 'default' | 'warning' | 'danger' => {
  if (progress >= 80) {
    return 'success';
  }
  if (progress >= 60) {
    return 'default';
  }
  if (progress >= 40) {
    return 'warning';
  }
  return 'danger';
};

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  progress,
  className = '',
  onClick,
  showProgress = true,
  showCategory = true
}) => {
  const categoryDisplayName = goalsService.getCategoryDisplayName(goal.category);
  const categoryColorClass = goalsService.getCategoryColorClass(goal.category);
  
  const trendingInfo = progress ? goalsService.getTrendingDisplayInfo(progress.trending) : null;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`
        bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200
        ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}
        ${className}
      `}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `View details for ${goal.name}` : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {goal.name}
          </h3>
          {showCategory && (
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full border ${categoryColorClass}`}>
              {categoryDisplayName}
            </span>
          )}
        </div>
        {progress && trendingInfo && (
          <StatusBadge 
            status={progress.trending as 'good' | 'neutral' | 'bad' | 'ahead' | 'upward' | 'maintaining' | 'slowing' | 'declining' | 'none'}
            size="sm"
            className="ml-3"
          />
        )}
      </div>

      {/* Description */}
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {goal.description}
      </p>

      {/* Progress Section */}
      {showProgress && progress && (
        <div className="space-y-3">
          {/* Current Value and Target */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">{progress.currentTitle}:</span>
              <span className="font-medium text-gray-900">{progress.currentValue}</span>
              {progress.deltaValue && (
                <span className={`text-xs px-2 py-1 rounded-full ${getDeltaColorClass(progress.deltaValue)}`}>
                  {progress.deltaValue}
                </span>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <ProgressBar
            progress={progress.progress}
            variant={getProgressVariant(progress.progress)}
            size="md"
            animated
          />

          {/* Target Text */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{progress.targetText}</span>
            <span>{progress.progress.toFixed(0)}% complete</span>
          </div>
        </div>
      )}

      {/* Sub-goals */}
      {goal.subGoals && goal.subGoals.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-600 mb-2">Related goals:</div>
          <div className="flex flex-wrap gap-2">
            {goal.subGoals.slice(0, 3).map((subGoal) => (
              <span
                key={subGoal.id}
                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
              >
                {subGoal.name}
              </span>
            ))}
            {goal.subGoals.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                +{goal.subGoals.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Advice */}
      {progress?.advice && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              {progress.advice.title}
            </h4>
            <p className="text-sm text-blue-800">
              {progress.advice.body}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};